import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CodeOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { message, Modal } from 'antd';
import type { GoogleUser } from '../App';
import * as api from '../api/client';
import type { AgentRecord, ApiTokenSummary } from '../api/client';

type ApiKeyType = 'dev' | 'prod';
type TokenWithGrants = ApiTokenSummary & { allowed_agent_ids?: string[] };

interface ApiKeyFormValues {
  name: string;
  token_type: ApiKeyType;
  monthly_limit_enabled: boolean;
  monthly_limit: number;
  pii_restrictions_enabled: boolean;
  allowed_agent_ids: string[];
}

const DEFAULT_FORM: ApiKeyFormValues = {
  name: '',
  token_type: 'dev',
  monthly_limit_enabled: false,
  monthly_limit: 1000,
  pii_restrictions_enabled: false,
  allowed_agent_ids: [],
};

const allowedAgentsFromToken = (token?: TokenWithGrants | null): string[] => {
  if (!token) return [];
  if (Array.isArray(token.allowed_agent_ids)) return token.allowed_agent_ids.filter(Boolean);
  return (token.scopes || [])
    .map(scope => String(scope || '').trim())
    .filter(scope => scope.startsWith('agent:') && scope.endsWith(':execute') && scope !== 'agent:execute')
    .map(scope => scope.slice('agent:'.length, -':execute'.length))
    .filter(Boolean);
};

const dedupeAgents = (agents: AgentRecord[]): AgentRecord[] => {
  const seen = new Set<string>();
  return agents
    .filter(agent => {
      if (!agent?.id || seen.has(agent.id)) return false;
      seen.add(agent.id);
      return true;
    })
    .sort((a, b) => {
      const apiSort = Number(!a.allow_api_execution) - Number(!b.allow_api_execution);
      return apiSort || (a.name || '').localeCompare(b.name || '');
    });
};

interface ApiKeyModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  initialValue: ApiKeyFormValues;
  saving: boolean;
  agents: AgentRecord[];
  agentsLoading: boolean;
  onCancel: () => void;
  onSave: (values: ApiKeyFormValues) => void;
}

const ApiKeyModal: React.FC<ApiKeyModalProps> = ({
  open,
  mode,
  initialValue,
  saving,
  agents,
  agentsLoading,
  onCancel,
  onSave,
}) => {
  const [form, setForm] = useState<ApiKeyFormValues>(initialValue);

  useEffect(() => {
    if (open) setForm(initialValue);
  }, [open, initialValue]);

  const selected = new Set(form.allowed_agent_ids);

  const toggleAgent = (agentId: string, checked: boolean) => {
    setForm(prev => {
      const next = new Set(prev.allowed_agent_ids);
      if (checked) next.add(agentId);
      else next.delete(agentId);
      return { ...prev, allowed_agent_ids: Array.from(next) };
    });
  };

  const save = () => {
    if (!form.name.trim()) {
      message.error('Key Name is required.');
      return;
    }
    onSave(form);
  };

  return (
    <Modal open={open} onCancel={onCancel} footer={null} centered width={620} destroyOnClose title={null} closable>
      <div className="praxys-apikey-modal-body">
        <h2 className="praxys-apikey-modal-title">{mode === 'create' ? 'Create API Key' : 'Edit API Key'}</h2>
        <p className="praxys-apikey-modal-subtitle">Assign this key to the agents external apps can execute.</p>

        <div className="praxys-token-form-field">
          <label className="praxys-token-label" htmlFor="ak-name">Key Name</label>
          <input
            id="ak-name"
            className="praxys-modal-input"
            placeholder="e.g. CRM Integration"
            value={form.name}
            onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
            autoFocus
          />
        </div>

        <div className="praxys-token-form-field">
          <label className="praxys-token-label">Key Type</label>
          <div className="praxys-apikey-type-card praxys-apikey-type-card--selected">
            <CodeOutlined className="praxys-apikey-type-card-icon" />
            <div>
              <div className="praxys-apikey-type-card-title">Development</div>
              <div className="praxys-apikey-type-card-subtitle">Rate limited to 100 requests/minute</div>
            </div>
          </div>
        </div>

        <div className="praxys-token-form-field">
          <label className="praxys-token-label">
            Allowed Agents <span className="praxys-apikey-modal-label-hint">– Assign this token to an Agent</span>
          </label>
          <div style={{ border: '1px solid #333', borderRadius: 12, background: '#171717', padding: 10, maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {agentsLoading ? (
              <div style={{ color: '#9ca3af', fontSize: 12 }}>Loading agents…</div>
            ) : agents.length === 0 ? (
              <div style={{ color: '#9ca3af', fontSize: 12 }}>No agents found. Create an agent first, then return here.</div>
            ) : agents.map(agent => {
              const disabled = !agent.allow_api_execution;
              return (
                <label key={agent.id} className="praxys-token-scope-item" style={{ alignItems: 'flex-start', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1 }}>
                  <input
                    type="checkbox"
                    className="praxys-token-scope-checkbox"
                    checked={selected.has(agent.id)}
                    disabled={disabled}
                    onChange={event => toggleAgent(agent.id, event.target.checked)}
                  />
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span className="praxys-token-label">{agent.name}</span>
                    <span className="praxys-apikey-modal-label-hint" style={{ fontSize: 11 }}>
                      {agent.visibility} · {agent.agent_type}{disabled ? ' · API execution disabled' : ' · API execution enabled'}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
          {form.allowed_agent_ids.length > 0 && (
            <div style={{ color: '#10a37f', fontSize: 12, marginTop: 4 }}>
              This key can execute {form.allowed_agent_ids.length} assigned agent{form.allowed_agent_ids.length === 1 ? '' : 's'}.
            </div>
          )}
        </div>

        <div className="praxys-token-form-field">
          <label className="praxys-token-scope-item" style={{ marginBottom: 6 }}>
            <input
              type="checkbox"
              className="praxys-token-scope-checkbox"
              checked={form.monthly_limit_enabled}
              onChange={e => setForm(prev => ({ ...prev, monthly_limit_enabled: e.target.checked }))}
            />
            <span className="praxys-token-label" style={{ cursor: 'pointer' }}>Limit monthly usage</span>
          </label>
          <input
            className="praxys-modal-input"
            type="number"
            min={1}
            value={form.monthly_limit}
            onChange={e => setForm(prev => ({ ...prev, monthly_limit: Math.max(1, Number(e.target.value)) }))}
            disabled={!form.monthly_limit_enabled}
            aria-label="Monthly usage limit"
            style={{ opacity: form.monthly_limit_enabled ? 1 : 0.45 }}
          />
        </div>

        <div className="praxys-token-form-field">
          <label className="praxys-token-scope-item">
            <input
              type="checkbox"
              className="praxys-token-scope-checkbox"
              checked={form.pii_restrictions_enabled}
              onChange={e => setForm(prev => ({ ...prev, pii_restrictions_enabled: e.target.checked }))}
            />
            <span className="praxys-token-label" style={{ cursor: 'pointer' }}>Enable PII Restrictions</span>
          </label>
        </div>

        <div className="praxys-apikey-modal-footer">
          <button type="button" className="praxys-apikey-modal-btn praxys-apikey-modal-btn--primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
          <button type="button" className="praxys-apikey-modal-btn praxys-apikey-modal-btn--secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

interface ApiKeyRowProps {
  token: TokenWithGrants;
  fullToken: string | undefined;
  revealed: boolean;
  copied: boolean;
  revoking: boolean;
  agentNameById: Record<string, string>;
  onReveal: (id: string) => void;
  onCopy: (id: string) => void;
  onEdit: (token: TokenWithGrants) => void;
  onDelete: (token: TokenWithGrants) => void;
}

const ApiKeyRow: React.FC<ApiKeyRowProps> = ({ token, fullToken, revealed, copied, revoking, agentNameById, onReveal, onCopy, onEdit, onDelete }) => {
  const inactive = token.revoked || token.expired;
  const maskedKey = `${token.token_prefix}${'*'.repeat(20)}`;
  const displayKey = revealed && fullToken ? fullToken : maskedKey;
  const assignedAgentIds = allowedAgentsFromToken(token);
  const assignedNames = assignedAgentIds.map(id => agentNameById[id] || id).slice(0, 3);
  const canReveal = Boolean(fullToken);

  return (
    <tr className={`praxys-apikey-row${inactive ? ' praxys-apikey-row--inactive' : ''}`}>
      <td className="praxys-apikey-cell praxys-apikey-cell--name">
        <span className="praxys-apikey-name">{token.name}</span>
        {token.revoked && <span className="praxys-token-badge--revoked praxys-apikey-status-badge">Revoked</span>}
        {!token.revoked && token.expired && <span className="praxys-token-badge--expired praxys-apikey-status-badge">Expired</span>}
        {assignedAgentIds.length > 0 && (
          <div className="praxys-token-meta-row" title={assignedNames.join(', ')}>
            <span className="praxys-token-scope-tag praxys-token-scope-tag--full">{assignedAgentIds.length} Agent{assignedAgentIds.length === 1 ? '' : 's'} assigned</span>
            {assignedNames.map(name => <span key={`${token.id}-${name}`} className="praxys-token-scope-tag">{name}</span>)}
          </div>
        )}
      </td>
      <td className="praxys-apikey-cell"><span className="praxys-apikey-type-badge"><CodeOutlined style={{ fontSize: 11 }} />{token.token_type === 'prod' ? 'prod' : 'dev'}</span></td>
      <td className="praxys-apikey-cell praxys-apikey-cell--usage">{token.usage_count ?? 0}</td>
      <td className="praxys-apikey-cell praxys-apikey-cell--key"><span className="praxys-apikey-key-pill" title={displayKey}>{displayKey}</span></td>
      <td className="praxys-apikey-cell praxys-apikey-cell--options">
        <div className="praxys-apikey-options">
          <button type="button" className={`praxys-apikey-icon-btn${!canReveal ? ' praxys-apikey-icon-btn--disabled' : ''}`} onClick={() => canReveal && onReveal(token.id)} aria-label={revealed ? 'Hide API key' : 'Reveal API key'} title={canReveal ? (revealed ? 'Hide key' : 'Reveal key') : 'Full key only available immediately after creation'} disabled={!canReveal}>{revealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}</button>
          <button type="button" className={`praxys-apikey-icon-btn${copied ? ' praxys-apikey-icon-btn--success' : ''}${!canReveal ? ' praxys-apikey-icon-btn--disabled' : ''}`} onClick={() => onCopy(token.id)} aria-label="Copy API key" title={canReveal ? (copied ? 'Copied!' : 'Copy key') : 'Full key only available immediately after creation'} disabled={!canReveal}><CopyOutlined /></button>
          {!inactive && <button type="button" className="praxys-apikey-icon-btn" onClick={() => onEdit(token)} aria-label="Edit API key" title="Edit key"><EditOutlined /></button>}
          <button type="button" className="praxys-apikey-icon-btn praxys-apikey-icon-btn--danger" onClick={() => onDelete(token)} disabled={revoking} aria-label="Delete API key" title="Delete key"><DeleteOutlined /></button>
        </div>
      </td>
    </tr>
  );
};

interface Props {
  user: GoogleUser | null;
}

const ApiTokensView: React.FC<Props> = ({ user }) => {
  const [tokens, setTokens] = useState<TokenWithGrants[]>([]);
  const [agents, setAgents] = useState<AgentRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [fullTokenMap, setFullTokenMap] = useState<Record<string, string>>({});
  const [revealedIds, setRevealedIds] = useState<Set<string>>(new Set());
  const [copiedIds, setCopiedIds] = useState<Set<string>>(new Set());
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingToken, setEditingToken] = useState<TokenWithGrants | null>(null);
  const [modalInitialValues, setModalInitialValues] = useState<ApiKeyFormValues>(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const copyTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const userId = user?.email || '';

  const agentNameById = useMemo(() => agents.reduce<Record<string, string>>((acc, agent) => {
    acc[agent.id] = agent.name;
    return acc;
  }, {}), [agents]);

  const fetchTokens = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setFetchError('');
    try {
      const res = await api.listApiTokens(userId);
      setTokens(Array.isArray(res.data?.tokens) ? res.data.tokens as TokenWithGrants[] : []);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404 || status === 422) setTokens([]);
      else setFetchError('Failed to load API tokens. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const fetchAgents = useCallback(async () => {
    if (!userId) return;
    setAgentsLoading(true);
    try {
      const settled = await Promise.allSettled([api.listAgents('my'), api.listAgents('shared'), api.listAgents('subscribed')]);
      const allAgents = settled.flatMap(result => (
        result.status === 'fulfilled' && Array.isArray(result.value.data?.agents) ? result.value.data.agents : []
      ));
      setAgents(dedupeAgents(allAgents));
    } catch {
      setAgents([]);
    } finally {
      setAgentsLoading(false);
    }
  }, [userId]);

  useEffect(() => { void fetchTokens(); }, [fetchTokens]);
  useEffect(() => { void fetchAgents(); }, [fetchAgents]);

  const openCreate = () => {
    setEditingToken(null);
    setModalInitialValues(DEFAULT_FORM);
    setModalMode('create');
    setModalOpen(true);
    if (!agents.length && !agentsLoading) void fetchAgents();
  };

  const openEdit = (token: TokenWithGrants) => {
    setEditingToken(token);
    setModalInitialValues({
      name: token.name,
      token_type: token.token_type ?? 'dev',
      monthly_limit_enabled: token.monthly_limit_enabled ?? false,
      monthly_limit: token.monthly_limit || 1000,
      pii_restrictions_enabled: token.pii_restrictions_enabled ?? false,
      allowed_agent_ids: allowedAgentsFromToken(token),
    });
    setModalMode('edit');
    setModalOpen(true);
    if (!agents.length && !agentsLoading) void fetchAgents();
  };

  const handleModalSave = async (values: ApiKeyFormValues) => {
    if (!userId) return;
    setSaving(true);
    const scopes = values.allowed_agent_ids.length ? ['agent:execute'] : [];
    try {
      if (modalMode === 'create') {
        const res = await api.createApiToken({
          user_id: userId,
          name: values.name.trim(),
          scopes,
          allowed_agent_ids: values.allowed_agent_ids,
          expires_at: null,
        } as any);
        if (values.monthly_limit_enabled || values.pii_restrictions_enabled || values.token_type !== 'dev') {
          await api.updateApiToken(res.data.id, {
            user_id: userId,
            name: values.name.trim(),
            token_type: values.token_type,
            monthly_limit: values.monthly_limit,
            monthly_limit_enabled: values.monthly_limit_enabled,
            pii_restrictions_enabled: values.pii_restrictions_enabled,
            scopes,
            allowed_agent_ids: values.allowed_agent_ids,
          } as any);
        }
        setFullTokenMap(prev => ({ ...prev, [res.data.id]: res.data.token }));
        setRevealedIds(prev => new Set(Array.from(prev).concat(res.data.id)));
        message.success(`API key "${values.name}" created. Copy it now; it will not be shown again.`, 6);
      } else if (editingToken) {
        await api.updateApiToken(editingToken.id, {
          user_id: userId,
          name: values.name.trim(),
          token_type: values.token_type,
          monthly_limit: values.monthly_limit,
          monthly_limit_enabled: values.monthly_limit_enabled,
          pii_restrictions_enabled: values.pii_restrictions_enabled,
          scopes,
          allowed_agent_ids: values.allowed_agent_ids,
        } as any);
        message.success('API key updated.');
      }
      await fetchTokens();
      setModalOpen(false);
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Operation failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleReveal = (tokenId: string) => {
    setRevealedIds(prev => {
      const next = new Set(Array.from(prev));
      next.has(tokenId) ? next.delete(tokenId) : next.add(tokenId);
      return next;
    });
  };

  const handleCopy = async (tokenId: string) => {
    const full = fullTokenMap[tokenId];
    if (!full) {
      message.warning('The full API key is only available immediately after creation. Create or rotate a key to copy a usable token.');
      return;
    }
    try {
      await navigator.clipboard.writeText(full);
      setCopiedIds(prev => new Set(Array.from(prev).concat(tokenId)));
      if (copyTimers.current[tokenId]) clearTimeout(copyTimers.current[tokenId]);
      copyTimers.current[tokenId] = setTimeout(() => {
        setCopiedIds(prev => {
          const next = new Set(Array.from(prev));
          next.delete(tokenId);
          return next;
        });
      }, 2000);
      message.success('API key copied to clipboard.');
    } catch {
      message.error('Clipboard access denied. Please copy the key manually.');
    }
  };

  const handleDelete = (token: TokenWithGrants) => {
    Modal.confirm({
      className: 'praxys-dark-confirm-modal',
      title: 'Delete API Key',
      content: `Are you sure you want to permanently delete "${token.name}"? This cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setRevokingId(token.id);
        try {
          await api.revokeApiToken(token.id, userId);
          setTokens(prev => prev.filter(t => t.id !== token.id));
          setFullTokenMap(prev => {
            const next = { ...prev };
            delete next[token.id];
            return next;
          });
          message.success(`API key "${token.name}" deleted.`);
        } catch {
          message.error('Failed to delete token. Please try again.');
        } finally {
          setRevokingId(null);
        }
      },
    });
  };

  const activeTokens = tokens.filter(t => !t.revoked && !t.expired);
  const inactiveTokens = tokens.filter(t => t.revoked || t.expired);

  return (
    <section className="praxys-apikey-card">
      <div className="praxys-apikey-card-header">
        <h3 className="praxys-apikey-card-title">API Keys</h3>
        <button type="button" className="praxys-apikey-add-btn" onClick={openCreate} aria-label="Add new API key" title="Add new API key"><PlusOutlined /></button>
      </div>

      {fetchError && <div className="praxys-apikey-error-banner" role="alert">{fetchError}<button type="button" className="praxys-apikey-retry-btn" onClick={fetchTokens}>Retry</button></div>}

      {loading ? (
        <div className="praxys-view-empty">Loading API keys…</div>
      ) : tokens.length === 0 && !fetchError ? (
        <div className="praxys-view-empty">No API keys yet. <button type="button" className="praxys-apikey-inline-link" onClick={openCreate}>Create one</button> to get started.</div>
      ) : (
        <div className="praxys-apikey-table-wrapper">
          <table className="praxys-apikey-table" aria-label="API Keys">
            <thead>
              <tr className="praxys-apikey-thead-row"><th className="praxys-apikey-th">NAME</th><th className="praxys-apikey-th">TYPE</th><th className="praxys-apikey-th">USAGE</th><th className="praxys-apikey-th">KEY</th><th className="praxys-apikey-th praxys-apikey-th--options">OPTIONS</th></tr>
            </thead>
            <tbody>
              {activeTokens.map(t => <ApiKeyRow key={t.id} token={t} fullToken={fullTokenMap[t.id]} revealed={revealedIds.has(t.id)} copied={copiedIds.has(t.id)} revoking={revokingId === t.id} agentNameById={agentNameById} onReveal={handleReveal} onCopy={handleCopy} onEdit={openEdit} onDelete={handleDelete} />)}
              {inactiveTokens.length > 0 && <tr><td colSpan={5} className="praxys-apikey-section-label">Revoked / Expired</td></tr>}
              {inactiveTokens.map(t => <ApiKeyRow key={t.id} token={t} fullToken={fullTokenMap[t.id]} revealed={revealedIds.has(t.id)} copied={copiedIds.has(t.id)} revoking={revokingId === t.id} agentNameById={agentNameById} onReveal={handleReveal} onCopy={handleCopy} onEdit={openEdit} onDelete={handleDelete} />)}
            </tbody>
          </table>
        </div>
      )}

      <ApiKeyModal open={modalOpen} mode={modalMode} initialValue={modalInitialValues} saving={saving} agents={agents} agentsLoading={agentsLoading} onCancel={() => setModalOpen(false)} onSave={handleModalSave} />
    </section>
  );
};

export default ApiTokensView;
